import React, { useEffect, useMemo, useRef } from 'react';
import { Select, SelectItem } from '@carbon/react';
import { useTransactionCategories } from 'src/hooks/useTransactionCategories';
import { TransactionCategoryTypeEnum } from 'src/utils/accounting';

interface TransactionCategorySelectorProps {
  value: string | undefined | null;
  type: TransactionCategoryTypeEnum;
  invalid?: boolean;
  /**
   * Receives the chosen category alongside its id. The caller needs the NAME to derive the
   * transaction title, and looking it up on its own side is what produced the bug: this
   * component and its parent each subscribe to the categories query separately, so the parent
   * could still be holding `undefined` on the render where this one already has the data.
   * Passing the row removes the possibility of the two disagreeing.
   */
  onChange: (value: string | null, category?: { id: string; name: string } | null) => void;
}

const TransactionCategorySelector = React.memo((props: TransactionCategorySelectorProps) => {
  const { onChange } = props;
  const { data: transactionCategories } = useTransactionCategories();

  // TransactionForm.tsx passes an inline `onChange` (a fresh closure every render, wrapping
  // setFieldValue), so including it in the effect's own dependency array used to loop: the
  // effect calls onChange -> the parent's Formik state changes -> the parent re-renders with
  // a NEW onChange closure -> the changed dependency reruns this effect -> which calls
  // onChange again, unconditionally, forever (this is what ran jsdom out of memory). The
  // effect only needs the LATEST onChange when it actually runs, not to rerun because
  // onChange's identity changed, so keep it in a ref instead of in the dependency array.
  // Assigned from its own effect rather than during render: oxlint's react(refs) rule flags
  // `ref.current = ...` in the render body itself (verified — it fires even when the
  // assignment is hidden inside a separate custom hook), so a plain render-time assignment
  // would add a new lint error against this repo's "no new oxlint errors" bar.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (transactionCategories?.data?.length) {
      const defaultTransactionCategoryId = transactionCategories?.data
        ?.filter((item) => item.type === props.type)
        .find(() => true)?.id;
      const defaultCategory = transactionCategories?.data?.find(
        (item) => item.id === defaultTransactionCategoryId
      );
      if (defaultTransactionCategoryId) {
        onChangeRef.current(defaultTransactionCategoryId, defaultCategory);
      } else {
        onChangeRef.current(null, null);
      }
    }
  }, [transactionCategories, props.type]);

  const transactionCategoriesSelectionItems = useMemo(() => {
    if (!props.type) {
      return transactionCategories?.data.map((item) => ({ id: item.id, text: item.name }));
    }
    return transactionCategories?.data
      ?.filter((item) => item.type === props.type)
      .map((item) => ({ id: item.id, text: item.name }));
  }, [transactionCategories?.data, props.type]);

  return (
    <div className={'cds--fullwidth'}>
      <label className={'cds--label'} htmlFor={'transaction-category-selector'}>
        Select a category
      </label>
      <Select
        labelText="Select an option"
        hideLabel
        id={'transaction-category-selector'}
        onChange={(e: any) =>
          props.onChange(
            e.target.value,
            transactionCategories?.data?.find((item) => item.id === e.target.value)
          )
        }
        invalid={!props.value && transactionCategoriesSelectionItems?.length}
        defaultValue={props.value}
      >
        {transactionCategoriesSelectionItems?.map((item) => {
          return <SelectItem value={item.id} key={item.id} text={item.text} />;
        })}
      </Select>
    </div>
  );
});

export default TransactionCategorySelector;
